import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, MapPinIcon, SearchIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { inscriptionCategories, inscriptionChild, type ActivitySlot } from '../../data/loisirs';
import type { InscriptionKind } from '../../hooks/useInscriptionConversation';
interface InscriptionMessageProps {
  kind: InscriptionKind;
  live: boolean;
  category: string | null;
  slots: ActivitySlot[];
  slot: ActivitySlot | null;
  onPickCategory: (category: string) => void;
  onRegister: (slot: ActivitySlot) => void;
  onRestart: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
function SlotDetails({
  slot
}: {
  slot: ActivitySlot;
}) {
  return <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-1480" data-mp="13t89jotr0dni">
      <div className="flex items-center gap-1.5" data-id="element-1481" data-mp="13rnhc5uwp6st">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1482" data-mp="ytc21flue437" />
        <dt className="sr-only" data-id="element-1483" data-mp="tw3jvf0p201y">Schedule</dt>
        <dd data-id="element-1484" data-mp="txeplkdp9hvp">
          {slot.day} · {slot.time}
        </dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1485" data-mp="ox0q4qq5zg9a">
        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1486" data-mp="k5imfuwrb1km" />
        <dt className="sr-only" data-id="element-1487" data-mp="k3o66twfjxmv">Location</dt>
        <dd data-id="element-1488" data-mp="g4yrmxznoa1n">{slot.place}</dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1489" data-mp="g1t4hoal7i8k">
        <UsersIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1490" data-mp="b5lwzr2jlyoi" />
        <dt className="sr-only" data-id="element-1491" data-mp="6698l1w0wjjt">Ages and spots</dt>
        <dd data-id="element-1492" data-mp="64y93e9m1xyi">
          {slot.audience} · {slot.spots}
        </dd>
      </div>
    </dl>;
}
export function InscriptionMessage({
  kind,
  live,
  category,
  slots,
  slot,
  onPickCategory,
  onRegister,
  onRestart
}: InscriptionMessageProps) {
  if (kind === 'activite') {
    return <div data-id="element-1493" data-mp="3sp2va5jrc62f">
        <p className={eyebrow} data-id="element-1494" data-mp="3sot5ahyppywq">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1495" data-mp="3enqi21vzeik8" />
          Registration
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1496" data-mp="3ipbwhro5z4wj">Which activity would you like?</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1497" data-mp="a9im7ukfepnx">
          I am looking for open spots for {inscriptionChild.name} ({inscriptionChild.age}).
        </p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1498" data-mp="f7trdrzs0bzt">
            {inscriptionCategories.map(option => <li key={option} data-id="element-1499" data-mp="1goyr9utsjyz">
                <button type="button" onClick={() => onPickCategory(option)} className={chipButton} data-id="element-1500" data-mp="5isdq8gpe6p0">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'resultats') {
    const [best, ...others] = slots;
    if (!best) {
      return <div data-id="element-1501" data-mp="y0xqhsvrjh1t">
          <p className={eyebrow} data-id="element-1502" data-mp="2ipi626ynfku4">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1503" data-mp="2iogmfe3khq0s" />
            Search
          </p>
          <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1504" data-mp="2dpdsaop4l47l">
            No spots found for that activity.
          </p>
          <p className="mt-1 text-sm text-slate-500" data-id="element-1505" data-mp="2sejnm1kueaa7">Name another activity and I will look again.</p>
        </div>;
    }
    return <div data-id="element-1506" data-mp="2nikwi684qe63">
        <p className={eyebrow} data-id="element-1507" data-mp="1zqe1pbi1yf8s">
          <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1508" data-mp="1zrp53a7l5kzd" />
          {slots.length} spots found in {category}
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1509" data-mp="1utdwt3wxvij9">
          Here is what I recommend for {inscriptionChild.name}
        </p>

        <article className="mt-4 rounded-2xl border-2 border-ville-navy bg-ville-sand p-4" data-id="element-1510" data-mp="24q0exkxvrvhl">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1511" data-mp="3l3qlcych2vue">
            <span className="rounded-full bg-ville-lime px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-ville-navy" data-id="element-1512" data-mp="3l25rzc1w9iy4">
              Recommended
            </span>
            <p className="text-[15px] font-extrabold text-ville-navy" data-id="element-1513" data-mp="1pp3ls0bgf0lt">{best.name}</p>
          </div>
          <SlotDetails slot={best} data-id="element-1514" data-mp="1unoe7dkgsfzp" />
          {best.note && <p className="mt-2 text-[13px] font-semibold text-ville-deep" data-id="element-1515" data-mp="1yogng6tc6kvz">{best.note}</p>}
          <p className="mt-2 text-[13px] font-bold text-ville-navy" data-id="element-1516" data-mp="1yqtd765c5zjl">{best.price}</p>
          {live && <button type="button" onClick={() => onRegister(best)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 sm:w-auto" data-id="element-1517" data-mp="23l716nho54e8">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1518" data-mp="2md9kk6lp2ys6" />
              Register {inscriptionChild.name} — one click
            </button>}
        </article>

        {others.length > 0 && <ul className="mt-3 space-y-2" data-id="element-1519" data-mp="2wd0f28a99ds9">
            {others.map(option => <li key={option.id} data-id="element-1520" data-mp="36gp0vv9sd57w">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-1521" data-mp="36hqot0bwhm71">
                  <div className="min-w-0" data-id="element-1522" data-mp="31jfcmiy4uzln">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-1523" data-mp="2wj146zsmdnn3">
                      {option.day} · {option.time}
                    </p>
                    <p className="truncate text-xs text-slate-500" data-id="element-1524" data-mp="2siha653cf056">
                      {option.place} · {option.spots}
                    </p>
                  </div>
                  {live && <button type="button" onClick={() => onRegister(option)} className={chipButton} data-id="element-1525" data-mp="3k83uew1bkot7">
                      Choose
                    </button>}
                </div>
              </li>)}
          </ul>}
      </div>;
  }
  return <div data-id="element-1526" data-mp="3bdfp55vqv74e">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1527" data-mp="brqvz0hc8oy4">
        <CheckIcon className="h-6 w-6" data-id="element-1528" data-mp="70sdbu8vnerp" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1529" data-mp="6yxsefxlvmnm">
        {inscriptionChild.name} is registered
      </h3>
      {slot && <>
          <p className="mt-1 text-[15px] font-bold text-ville-navy" data-id="element-1530" data-mp="266xpz5g7fd7j">{slot.name}</p>
          <SlotDetails slot={slot} data-id="element-1531" data-mp="1rjusgyrvt4k7" />
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-slate-600" data-id="element-1532" data-mp="1rijlyr71xipe">
            <CreditCardIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1533" data-mp="1wguxd6czw19q" />
            {slot.price} · paid with the saved card
          </p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-1534" data-mp="1nh7x4nw051i9">
            Confirmation sent by email · spot held with the family’s citizen card
          </p>
        </>}
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1535" data-mp="18mbh3ohyb9um">
          <Link to="/my-recreation" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1536" data-mp="1dkd1ccr45f7o">
            View my activities
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1537" data-mp="37uyo83oad9ak" />
          </Link>
          <button type="button" className={`inline-flex items-center gap-2 ${chipButton}`} data-id="element-1538" data-mp="w7wo533im8kn">
            <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1539" data-mp="rcpllylzf9xb" />
            Add to calendar
          </button>
          <button type="button" onClick={onRestart} className={chipButton} data-id="element-1540" data-mp="161lwe3ffb11m">
            Register for something else
          </button>
        </div>}
    </div>;
}